// Nova brand tokens, per the Nova.ai spec deck. Nova runs its own light/dark
// toggle independent of the rest of the site, so these are plain values
// consumed via inline style / conditional classes rather than Tailwind's
// dark: variant (not configured in this project).

export const novaYellow = {
  high: '#F3FE88', // Acid · dark-mode accent / gradient stop 1
  g1: '#F3EF73', // Citron · gradient stop 2
  g2: '#FDC212', // Amber · gradient stop 3 / light-mode action accent / resting mark
  g3: '#F5851F', // Orange · gradient stop 4
  g4: '#F15026', // Ember · gradient stop 5 (gradient-only, not an error colour)
  rest: '#FDC212', // Amber · light-mode action accent / resting mark
};

// Signal · the online status dot and the "copied"/success tick. The only colour in
// the system that is neither a warm grey nor a Nova gradient stop — fixed across
// both themes rather than living inside novaThemes below.
export const novaSignal = '#5FA777';

export type NovaThemeName = 'light' | 'dark';

export const novaThemes = {
  light: {
    actionAccent: novaYellow.rest,
    restingMark: novaYellow.rest,
    ink: {
      onGold: '#1B190F',
      userBubbleText: '#FCFCFA',
      heading: '#1B190F',
      body: '#4A463C',
      standfirst: '#4A463C',
      secondary: '#6B6659',
      placeholder: '#928D80',
      chromeIcon: '#8A867B',
      eyebrow: '#B4AFA3',
    },
    surfaces: {
      card: '#FFFFFF',
      panelBase: '#FCFCFA',
      field: '#F6F4ED',
      novaBubble: '#F3F1EA',
      userBubble: '#1B190F',
      activeRow: '#EDE8DD',
      hairline: '#EDEAE1',
      panelBorder: '#E7E4DA',
      composerBorder: '#E7E4DA',
      toggleTrackOff: '#EDEAE1',
      themeToggleTrack: '#F0EDE4',
      themeToggleTrackBorder: '#E7E4DA',
      themeToggleActive: '#FFFFFF',
    },
  },
  dark: {
    actionAccent: novaYellow.high,
    restingMark: novaYellow.high,
    ink: {
      onGold: '#1B190F',
      userBubbleText: '#191713',
      heading: '#ECEAE3',
      body: '#ECEAE3',
      standfirst: '#D9D6CD',
      secondary: '#D9D6CD',
      placeholder: '#8F8C83',
      chromeIcon: '#8F8C83',
      eyebrow: '#8F8C83',
    },
    surfaces: {
      card: '#191713',
      panelBase: '#191713',
      field: '#2A271F',
      novaBubble: '#2A271F',
      userBubble: '#ECEAE3',
      activeRow: '#3A362D',
      hairline: 'rgba(143,140,131,.3)',
      panelBorder: '#2A271F',
      composerBorder: '#2A271F',
      toggleTrackOff: '#3A362D',
      themeToggleTrack: 'rgba(255,255,255,0.06)',
      themeToggleTrackBorder: 'rgba(143,140,131,.3)',
      themeToggleActive: 'rgba(255,255,255,0.14)',
    },
  },
} as const;

export const novaDisclaimer = {
  long: 'AI response aggregated from Stylus sources — not authored by a Stylus expert. Read the full reports.',
  short: 'Nova.ai can be wrong — always check sources.',
};

// Markdown tables from AVA render through marked's default table renderer with no
// styling of their own — these child-selector utilities give them borders, header
// shading and row striping wherever a Nova answer is rendered via dangerouslySetInnerHTML.
// Colours come from novaTableVars below rather than static utility classes, since Nova's
// light/dark theme is runtime state, not Tailwind's dark: variant.
// `table-fixed` (not `table-auto`) is deliberate: a streamed answer appends table
// cells token by token, and auto layout recomputes every column's width off
// whatever content has arrived so far — the table visibly resizes and the
// horizontal scrollbar creeps in and out as it fills. Fixed layout settles
// column widths immediately and stays put; `break-words` keeps long cell
// content wrapping instead of forcing the table wider again.
//
// The leading `break-words` (on the container itself, inherited by every
// descendant) covers the same problem outside tables too: while streaming, an
// in-progress markdown link (e.g. a citation's URL whose closing `)` hasn't
// arrived yet) renders as literal unbroken text until it completes. Without a
// wrap opportunity that forces the `max-w-[880px]` answer card wider than
// intended, which is what pushes NovaLanding's scroll pane into horizontal
// overflow and shows a scrollbar mid-answer.
export const novaRichTextTableClassName =
  'break-words ' +
  '[&_table]:my-4 [&_table]:w-full [&_table]:table-fixed [&_table]:border-collapse [&_table]:text-left ' +
  '[&_th]:border [&_th]:border-[color:var(--nova-hairline)] [&_th]:bg-[color:var(--nova-table-head)] [&_th]:px-3 [&_th]:py-2.5 [&_th]:text-[13px] [&_th]:font-semibold [&_th]:break-words ' +
  '[&_td]:border [&_td]:border-[color:var(--nova-hairline)] [&_td]:px-3 [&_td]:py-2.5 [&_td]:align-top [&_td]:text-[14px] [&_td]:break-words ' +
  '[&_tbody_tr:nth-child(even)]:bg-[color:var(--nova-table-zebra)]';

export const novaTableVars = (surfaces: (typeof novaThemes)[NovaThemeName]) => ({
  '--nova-hairline': surfaces.surfaces.hairline,
  '--nova-table-head': surfaces.surfaces.field,
  '--nova-table-zebra': surfaces.surfaces.novaBubble,
  // Links follow Stylus: colour change only, never underlined.
  '--nova-link-hover': surfaces.actionAccent,
  // Consumed by novaWidgetRichTextClassName below to colour headings darker than body text.
  '--nova-heading-color': surfaces.ink.heading,
  // Consumed by linkCitationMarkers (novaAnswerParser) for every inline [n]
  // citation badge — text source or image alike — same filled-circle colours as
  // the badge already drawn over an image's own thumbnail, so every citation
  // reads as the same "chip" wherever it appears.
  '--nova-image-citation-bg': surfaces.surfaces.userBubble,
  '--nova-image-citation-fg': surfaces.ink.userBubbleText,
});

// Headings in a rendered answer need clear space above them and tight space below, so
// each one reads as the start of its section rather than another paragraph — the site's
// shared `.richText` only sets a bottom margin on headings, and the answer card's own
// `!my-2` overrides had made heading and paragraph spacing identical (8px). Applied
// to every answer body; `!mt-0` on the first child keeps a leading heading flush to the top.
export const novaRichTextHeadingSpacingClassName =
  '[&_h1]:!mt-6 [&_h1]:!mb-2 [&_h2]:!mt-6 [&_h2]:!mb-2 [&_h3]:!mt-5 [&_h3]:!mb-2 [&_h4]:!mt-4 [&_h4]:!mb-2 [&_h5]:!mt-4 [&_h5]:!mb-2 ' +
  '[&>:first-child]:!mt-0';

// The three heading styles of the finished answer card (NovaAnswerCard: the <h1> headline,
// SECTION_HEADING_CLASS, SUBSECTION_HEADING_CLASS), repeated with `!` so headings inside the
// streaming block — which sit under the site's `.richText` heading sizes — look the same
// while the answer is still arriving. Written out literally (not derived from the card's
// constants) because Tailwind only generates classes it can find in the source. Keep in
// step with the card.
export const novaStreamingHeadlineClassName =
  '!text-[28px] !leading-[32px] !font-semibold !tracking-[-0.6px] md:!text-[40px] md:!leading-[44px] md:!tracking-[-1.1px] !text-[color:var(--nova-heading-color)]';
export const novaStreamingSectionHeadingClassName =
  '!text-[24px] !leading-[30px] !font-semibold !tracking-[-0.4px] md:!text-[30px] md:!leading-[36px] md:!tracking-[-0.7px] !text-[color:var(--nova-heading-color)]';
export const novaStreamingSubsectionHeadingClassName =
  '!text-[19px] !leading-[25px] !font-semibold !tracking-[-0.2px] md:!text-[21px] md:!leading-[28px] md:!tracking-[-0.3px] !text-[color:var(--nova-heading-color)]';

export const novaSectionSubheadingClassName =
  '[&_h1]:!text-[17px] [&_h1]:!leading-6 [&_h1]:!font-semibold [&_h2]:!text-[17px] [&_h2]:!leading-6 [&_h2]:!font-semibold ' +
  '[&_h3]:!text-[17px] [&_h3]:!leading-6 [&_h3]:!font-semibold md:[&_h1]:!text-lg md:[&_h2]:!text-lg md:[&_h3]:!text-lg ' +
  '[&_h4]:!text-base [&_h4]:!leading-[22px] [&_h4]:!font-semibold [&_h5]:!text-[15px] [&_h5]:!leading-[22px] [&_h5]:!font-semibold';

// The widget's chat bubble (NovaMessage) renders whatever markdown the model
// returns through the site's shared `.richText` class, whose h1/h2/h3 sizes are
// tuned for full article pages (up to 70px) — far too large for a 530px-wide
// bubble. This overrides every heading level to Nova's own compact widget scale
// (see the buildout spec's widget answer: 27px headline / 16px body) instead of
// inheriting the site-wide article typography.
export const novaWidgetRichTextClassName =
  '[&_h1]:!mt-4 [&_h1]:!mb-1.5 [&_h1]:!text-[19px] [&_h1]:!leading-[24px] [&_h1]:!font-semibold [&_h1]:!tracking-[-0.3px] [&_h1]:!text-[color:var(--nova-heading-color)] ' +
  '[&_h2]:!mt-4 [&_h2]:!mb-1.5 [&_h2]:!text-[17px] [&_h2]:!leading-[22px] [&_h2]:!font-semibold [&_h2]:!text-[color:var(--nova-heading-color)] ' +
  '[&_h3]:!mt-4 [&_h3]:!mb-1.5 [&_h3]:!text-[17px] [&_h3]:!leading-[22px] [&_h3]:!font-semibold [&_h3]:!text-[color:var(--nova-heading-color)] ' +
  '[&_h4]:!mt-3 [&_h4]:!mb-1.5 [&_h4]:!text-[15px] [&_h4]:!leading-5 [&_h4]:!font-semibold [&_h4]:!text-[color:var(--nova-heading-color)] ' +
  '[&>:first-child]:!mt-0 ' +
  '[&_p]:!my-2 [&_p]:!text-[15px] [&_p]:!leading-[22px] ' +
  '[&_ul]:!my-2 [&_ul]:!text-[15px] [&_ul]:!leading-[22px] [&_ol]:!my-2 [&_ol]:!text-[15px] [&_ol]:!leading-[22px]';
